///
/// EarthBrowser
///
/// Created by Matt Giger on 12/30/2023
/// Copyright (c) 2023 Geodata Labs
///

import { geographic, wgs } from "@/earthbrowser/utils/geographic"
import { equals, lerp, round, trig } from "@stovokor/math/math"
import { vec3 } from "@stovokor/math/vector"

/**
 * Represents a point in spherical coordinates.
 */
export class spherical {
	/** A vector with all components set to zero. */
	static zero = new spherical(0, 0, 0)

	public readonly radius: number = 0
	public readonly theta: number = 0
	public readonly phi: number = 0

	/**
	 * Creates a new spherical coordinate instance from a cartesian vec3 or individual values.
	 * @param radius - The radial distance from the origin (default: 0)
	 * @param theta - The azimuthal angle in the x-y plane from the x-axis in radians (default: 0)
	 * @param phi - The polar angle from the z-axis in radians (default: 0)
	 */
	constructor(radius: vec3 | geographic | number | undefined = 0, theta: number | undefined = 0, phi: number | undefined = 0) {
		if(radius instanceof vec3) {
			this.radius = radius.length
			this.theta = Math.atan2(radius.x, radius.z)
			this.phi = Math.acos(radius.y / this.radius)
		} else if(radius instanceof geographic) {
			this.radius = radius.altitude + wgs.a
			this.theta = trig.pi - (radius.longitude + trig.half_pi)
			this.phi = radius.latitude
		} else {
			this.radius = radius
			this.theta = theta
			this.phi = phi
		}
	}

	/**
	 * Returns the spherical coordinates as an array.
	 * @returns An array containing [radius, theta, phi]
	 */
	public get array(): number[] {
		return [this.radius, this.theta, this.phi]
	}

	/**
	 * Converts the spherical coordinates to Cartesian coordinates.
	 * @returns A vec3 representing the point in Cartesian coordinates
	 */
	public get cartesian(): vec3 {
		const radius = this.radius
		const sinz = Math.sin(this.phi)
		return new vec3(
			radius * Math.sin(this.theta) * sinz,
			radius * Math.cos(this.phi),
			radius * Math.cos(this.theta) * sinz
		)
	}

	/**
	 * Converts the spherical coordinates to a geographic coordinate.
	 * @returns A geographic coordinate representing the point
	 */
	public get geographic(): geographic {
		return new geographic(this.cartesian)
	}

	/**
	 * Creates spherical coordinates by wrapping the destination coordinates to be closest to this source coordinate.
	 * Uses the actual source values (not normalized) to ensure smooth interpolation during spring animations.
	 * @param dest - The destination spherical coordinates
	 * @returns The spherical coordinates wrapped to be closest to the source coordinates
	 */
	public wrappedToClosest(dest: spherical): spherical {
		// Use actual source theta (don't normalize - the spring may have accumulated values)
		const sourceTheta = this.theta

		// Normalize destination theta to [0, 2π] range first
		let destTheta = dest.theta % trig.two_pi
		if (destTheta < 0) {
			destTheta += trig.two_pi
		}

		// Unwrap destination to be closest to actual source value
		// by adding/subtracting 2π until difference is in [-π, π]
		let theta = destTheta
		while (sourceTheta - theta > trig.pi) {
			theta += trig.two_pi
		}
		while (sourceTheta - theta < -trig.pi) {
			theta -= trig.two_pi
		}

		// Use actual source phi (don't normalize)
		const sourcePhi = this.phi

		// Normalize destination phi to [0, 2π] range first
		let destPhi = dest.phi % trig.two_pi
		if (destPhi < 0) {
			destPhi += trig.two_pi
		}

		// Unwrap destination phi to be closest to actual source value
		let phi = destPhi
		while (sourcePhi - phi > trig.pi) {
			phi += trig.two_pi
		}
		while (sourcePhi - phi < -trig.pi) {
			phi -= trig.two_pi
		}

		return new spherical(dest.radius, theta, phi)
	}

	public lerp(other: spherical, progress: number): spherical {
		const radius = lerp(this.radius, other.radius, progress)
		const phi = lerp(this.phi, other.phi, progress)

		const ttheta = this.theta + 2 * trig.two_pi
		const tother = other.theta + 2 * trig.two_pi
		const theta = lerp(ttheta, tother, progress) - 2 * trig.two_pi
		return new spherical(radius, theta, phi)
	}

	/**
	 * Returns a string representation of the spherical coordinates.
	 * @returns A string in the format "(radius: X m, theta: Y deg, phi: Z deg)"
	 */
	public toString(): string {
		return `(radius: ${round(this.radius, 2)} m, theta: ${round(this.theta * trig.deg, 2)} deg, phi: ${round(this.phi * trig.deg, 2)} deg}`
	}

	public equals(other: spherical): boolean {
		return equals(this.radius, other.radius) && equals(this.theta, other.theta) && equals(this.phi, other.phi)
	}
}
